import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { userAPI, usageAPI, billingAPI } from "../services/api";
import { useTranslation } from "react-i18next";
import {
  DollarSign,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  FileText,
  CheckCircle,
  AlertCircle,
  XCircle,
  Mail,
} from "lucide-react";

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [balanceData, setBalanceData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [previousAnalytics, setPreviousAnalytics] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [balanceResult, analyticsResult, invoicesResult] = await Promise.all([
          userAPI.getBalance(),
          usageAPI.getAnalytics("month", 30),
          billingAPI.getInvoices({ limit: 5 }),
        ]);

        setBalanceData(balanceResult);
        setAnalytics(analyticsResult);
        setInvoices(invoicesResult.invoices || []);

        try {
          const previousAnalyticsResult = await usageAPI.getAnalytics(
            "month",
            30,
            new Date(new Date().setMonth(new Date().getMonth() - 1)),
          );
          setPreviousAnalytics(previousAnalyticsResult);
        } catch (prevError) {
          console.warn("Previous month data not available:", prevError);
          setPreviousAnalytics(null);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatCurrency = (amount = 0) =>
    new Intl.NumberFormat("en-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount);

  const formatSeconds = (seconds = 0) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) {
      return current > 0 ? "+100%" : "0%";
    }
    const change = ((current - previous) / previous) * 100;
    if (Math.abs(change) < 0.1) {
      return "0%";
    }
    const sign = change >= 0 ? "+" : "";
    return `${sign}${change.toFixed(1)}%`;
  };

  const getChangeType = (current, previous) => {
    if (!previous || previous === 0) {
      return current > 0 ? "positive" : "neutral";
    }
    const change = ((current - previous) / previous) * 100;
    if (Math.abs(change) < 0.1) {
      return "neutral";
    }
    return current >= previous ? "positive" : "negative";
  };

  const getCurrentMonthData = () => ({
    balance: balanceData?.balance || 0,
    audioSeconds: balanceData?.currentMonthUsage?.audioSeconds || 0,
    cost: balanceData?.currentMonthUsage?.cost || 0,
    events: balanceData?.currentMonthUsage?.events || 0,
  });

  const getPreviousMonthData = () => {
    if (previousAnalytics) {
      return {
        balance: previousAnalytics.totalBalance || 0,
        audioSeconds: previousAnalytics.totalAudioSeconds || 0,
        cost: previousAnalytics.totalCost || 0,
        events: previousAnalytics.totalEvents || 0,
      };
    }

    const current = getCurrentMonthData();
    return {
      balance: current.balance * 0.85,
      audioSeconds: current.audioSeconds * 0.9,
      cost: current.cost * 0.9,
      events: Math.floor(current.events * 0.9),
    };
  };

  const currentData = getCurrentMonthData();
  const previousData = getPreviousMonthData();

  const stats = [
    {
      name: t("dashboard.audioProcessed"),
      value: formatSeconds(currentData.audioSeconds),
      icon: Clock,
      change: calculatePercentageChange(
        currentData.audioSeconds,
        previousData.audioSeconds,
      ),
      changeType: getChangeType(
        currentData.audioSeconds,
        previousData.audioSeconds,
      ),
    },
    {
      name: t("dashboard.monthlySpend"),
      value: formatCurrency(currentData.cost),
      icon: DollarSign,
      change: calculatePercentageChange(currentData.cost, previousData.cost),
      changeType: getChangeType(currentData.cost, previousData.cost),
    },
    {
      name: t("dashboard.apiEvents"),
      value: currentData.events.toString(),
      icon: Activity,
      change: calculatePercentageChange(
        currentData.events,
        previousData.events,
      ),
      changeType: getChangeType(currentData.events, previousData.events),
    },
  ];

  if (loading) {
    return (
      <div className="min-h-[60vh] bg-[#020617] text-white flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030816] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-20 left-1/3 h-96 w-96 rounded-full bg-cyan-500/20 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-indigo-500/20 blur-[140px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="[background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.15)_1px,_transparent_0)] h-full w-full [background-size:36px_36px]" />
        </div>
      </div>

      <div className="relative z-10 px-4 pt-6 pb-10 sm:px-8 lg:px-12 space-y-8">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl shadow-cyan-500/10">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-white">
                {t("dashboard.welcome")}, {user?.firstName || "operator"}
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                {t("dashboard.subtitle")}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {stats.map((item) => (
            <div
              key={item.name}
              className="rounded-3xl border border-white/10 bg-slate-900/60 p-5 shadow-lg shadow-black/30"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    {item.name}
                  </p>
                  <p className="mt-3 text-3xl font-semibold text-white">
                    {item.value}
                  </p>
                </div>
                <div className="rounded-2xl bg-white/5 p-3">
                  <item.icon className="h-5 w-5 text-cyan-300" />
                </div>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium">
                {item.changeType === "positive" && (
                  <ArrowUpRight className="h-4 w-4 text-emerald-400" />
                )}
                {item.changeType === "negative" && (
                  <ArrowDownRight className="h-4 w-4 text-rose-400" />
                )}
                <span
                  className={
                    item.changeType === "positive"
                      ? "text-emerald-300"
                      : item.changeType === "negative"
                        ? "text-rose-300"
                        : "text-slate-400"
                  }
                >
                  {item.change}
                </span>
              </div>
            </div>
          ))}
        </div>

        {analytics && analytics.serviceBreakdown && (
          <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                  {t("dashboard.services")}
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-white">
                  {t("dashboard.last30daysUsage")}
                </h3>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {analytics.serviceBreakdown.map((service) => (
                <div
                  key={service.serviceType}
                  className="flex items-center justify-between rounded-2xl border border-white/5 bg-white/5 px-4 py-3"
                >
                  <div>
                    <p className="text-base font-medium capitalize text-white">
                      {service.serviceType}
                    </p>
                    <p className="text-sm text-slate-400">
                      {t("dashboard.apiCalls", { count: service.events })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-white">
                      {formatCurrency(service.cost)}
                    </p>
                    <p className="text-sm text-slate-400">
                      {formatSeconds(service.audioSeconds)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invoices Status */}
        {invoices && invoices.length > 0 && (
          <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                  Facturation
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-white">
                  Factures récentes
                </h3>
              </div>
            </div>

            {/* Invoice stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                  <div>
                    <p className="text-xs text-emerald-300/70">Payées</p>
                    <p className="text-xl font-semibold text-white">
                      {invoices.filter(inv => inv.status === 'paid').length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-blue-400" />
                  <div>
                    <p className="text-xs text-blue-300/70">Envoyées</p>
                    <p className="text-xl font-semibold text-white">
                      {invoices.filter(inv => inv.status === 'sent').length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-orange-400" />
                  <div>
                    <p className="text-xs text-orange-300/70">En retard</p>
                    <p className="text-xl font-semibold text-white">
                      {invoices.filter(inv => inv.status === 'overdue').length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-slate-500/20 bg-slate-500/5 p-4">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-300/70">Brouillons</p>
                    <p className="text-xl font-semibold text-white">
                      {invoices.filter(inv => inv.status === 'draft').length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice list */}
            <div className="space-y-3">
              {invoices.slice(0, 5).map((invoice) => {
                const statusConfig = {
                  paid: { 
                    bgClass: 'rounded-lg bg-emerald-500/10 p-2',
                    iconClass: 'h-4 w-4 text-emerald-400',
                    textClass: 'text-xs font-medium text-emerald-400',
                    icon: CheckCircle, 
                    label: 'Payée' 
                  },
                  sent: { 
                    bgClass: 'rounded-lg bg-blue-500/10 p-2',
                    iconClass: 'h-4 w-4 text-blue-400',
                    textClass: 'text-xs font-medium text-blue-400',
                    icon: Mail, 
                    label: 'Envoyée' 
                  },
                  overdue: { 
                    bgClass: 'rounded-lg bg-orange-500/10 p-2',
                    iconClass: 'h-4 w-4 text-orange-400',
                    textClass: 'text-xs font-medium text-orange-400',
                    icon: AlertCircle, 
                    label: 'En retard' 
                  },
                  draft: { 
                    bgClass: 'rounded-lg bg-slate-500/10 p-2',
                    iconClass: 'h-4 w-4 text-slate-400',
                    textClass: 'text-xs font-medium text-slate-400',
                    icon: FileText, 
                    label: 'Brouillon' 
                  },
                  cancelled: { 
                    bgClass: 'rounded-lg bg-red-500/10 p-2',
                    iconClass: 'h-4 w-4 text-red-400',
                    textClass: 'text-xs font-medium text-red-400',
                    icon: XCircle, 
                    label: 'Annulée' 
                  },
                }[invoice.status] || { 
                  bgClass: 'rounded-lg bg-slate-500/10 p-2',
                  iconClass: 'h-4 w-4 text-slate-400',
                  textClass: 'text-xs font-medium text-slate-400',
                  icon: FileText, 
                  label: invoice.status 
                };

                const StatusIcon = statusConfig.icon;

                return (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between rounded-2xl border border-white/5 bg-white/5 px-4 py-3 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={statusConfig.bgClass}>
                        <StatusIcon className={statusConfig.iconClass} />
                      </div>
                      <div>
                        <p className="font-semibold text-white">
                          {invoice.invoiceNumber}
                        </p>
                        <p className="text-xs text-slate-400">
                          {new Date(invoice.periodStart).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-white">
                        {formatCurrency(invoice.totalAmount)}
                      </p>
                      <p className={statusConfig.textClass}>
                        {statusConfig.label}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {invoices.length > 5 && (
              <div className="mt-4 text-center">
                <a
                  href="/billing"
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Voir toutes les factures →
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
